/** @fileoverview Bootstraps Fabrik routes, theme, and signed-in user state. */

import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { isValidAppAddress } from './appMode/appAddress';
import AppModeRoot from './appMode/AppModeRoot';
import AppNotFoundView from './appMode/AppNotFoundView';
import { getCurrentAppsDomain } from './appMode/hostConfig';
import { readAppAddress } from './appMode/readAppAddress';
import FabrikShell from './components/FabrikShell';
import { Toaster } from './components/ui/toast';
import SessionExpiryDialog from './components/SessionExpiryDialog';
import { useTheme } from './hooks/useTheme';
import HandoverPage from './pages/HandoverPage';
import { getCurrentUser } from './services/platformAuth';
import type { User } from './types/identity';

/** The regular Fabrik builder: signs the user in and renders the shell. */
const BuilderApp = () => {
  const [user, setUser] = useState<User>();
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    getCurrentUser(controller.signal)
      .then(setUser)
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            reason instanceof Error ? reason.message : 'Unable to confirm your Platform sign-in.'
          );
        }
      });
    return () => controller.abort();
  }, []);

  return (
    <Toaster>
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<FabrikShell user={user} error={error} />} />
        </Routes>
        {user && <SessionExpiryDialog />}
      </BrowserRouter>
    </Toaster>
  );
};

/** Picks between the apps-domain app page, the hand-over page, and the builder. */
export default function App() {
  useTheme();
  const appAddress = readAppAddress(window.location.hostname, getCurrentAppsDomain());
  if (appAddress) {
    return isValidAppAddress(appAddress) ? (
      <AppModeRoot address={appAddress} />
    ) : (
      <AppNotFoundView />
    );
  }
  if (window.location.pathname === '/app-handover') {
    return <HandoverPage />;
  }
  return <BuilderApp />;
}
